import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { filter } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { RouteMetadataService } from './route-metadata.service';
import { LocalStorageUtils } from '../utils/user.utils';
import { eTypeUser } from '../constants/options-system';
import { STATIC_COMPANY_PAGES } from '../constants/static-pages';

const DEFAULT_TITLE = 'iPay Empresas';

/**
 * Título de la pestaña: "iPay Empresas | <opción>" para Business Admin,
 * "iPay Admin | <opción>" para Admin iPay. <opción> sale del mismo árbol de
 * system_option que arma el menú (ver RouteMetadataService); si la ruta activa
 * es una subpágina derivada (crear/editar/detalle, sin system_option propio),
 * usa el nombre de su opción padre.
 */
@Injectable({
  providedIn: 'root'
})
export class PageTitleService {
  private readonly router = inject(Router);
  private readonly titleService = inject(Title);
  private readonly authService = inject(AuthService);
  private readonly routeMetadataService = inject(RouteMetadataService);

  init(): void {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => this.updateTitle(event.urlAfterRedirects));

    this.updateTitle(this.router.url);
  }

  private async updateTitle(url: string): Promise<void> {
    const cleanUrl = url.split('?')[0];
    const companyMatch = cleanUrl.match(/^\/dashboard\/company\/?(.*)$/);

    if (!companyMatch) {
      this.titleService.setTitle(DEFAULT_TITLE);
      return;
    }

    const currentUser = this.authService.getCurrentUser()
      || LocalStorageUtils.getUserData(true)
      || LocalStorageUtils.getUserData(false);

    const prefix = currentUser?.user_type_id === eTypeUser.AdminBusiness
      ? 'iPay Empresas'
      : 'iPay Admin';

    const path = companyMatch[1];
    if (!path) {
      this.titleService.setTitle(prefix);
      return;
    }

    const staticPageName = STATIC_COMPANY_PAGES[path];
    if (staticPageName) {
      this.titleService.setTitle(`${prefix} | ${staticPageName}`);
      return;
    }

    const resolved = await this.routeMetadataService.resolve(path);
    this.titleService.setTitle(resolved ? `${prefix} | ${resolved.name}` : prefix);
  }
}
